import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../shared/auth/hooks/useAuth';

interface ProtectedCompanyRouteProps {
    children: React.ReactNode;
}

/**
 * Protected Route wrapper for Company/Recruiter pages
 * Ensures only authenticated company or recruiter users can access company routes
 * Candidates are redirected to their dashboard
 */
const ProtectedCompanyRoute: React.FC<ProtectedCompanyRouteProps> = ({ children }) => {
    const { isAuthenticated, isLoading, user } = useAuth();
    const location = useLocation();

    // Show loading state while checking authentication
    if (isLoading) {
        return (
            <div className="min-h-screen bg-[#F1F3F4] flex items-center justify-center">
                <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#015FA6] mb-4"></div>
                    <p className="text-base font-medium text-[#757575]">Loading...</p>
                </div>
            </div>
        );
    }

    // Redirect to login if not authenticated
    if (!isAuthenticated) {
        return <Navigate to="/auth/login" state={{ from: location.pathname }} replace />;
    }

    // Check if user is company, recruiter, or admin (admins can access company routes)
    const userRole = user?.userType;
    const hasCompanyAccess = userRole === 'company' || userRole === 'recruiter' || userRole === 'hush_admin';

    // Redirect candidates to their dashboard
    if (!hasCompanyAccess) {
        return <Navigate to="/candidate/dashboard" replace />;
    }

    // User is authenticated and has company access
    return <>{children}</>;
};

export default ProtectedCompanyRoute;
